/**
 * Lead Touchpoints Page - Fireflies strategy call recordings with enriched EOC + optin data
 */

import { useState, useEffect, useCallback } from "react";
import { Search, Play, DollarSign, ChevronUp, ChevronDown, ChevronsUpDown, User, Filter, X, Calendar } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { DataTable, type ColumnDef } from "../components/ui/data-table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CallDetailModal } from "../components/CallDetailModal";
import { branding } from "../config/branding";

// ─── Types ────────────────────────────────────────────────────────────────────

interface OptinDetail {
  date: number;
  source: string;
  isWebinar: boolean;
  webinarName: string;
}

interface StrategyCall {
  _id: string;
  firefliesId: string;
  title: string;
  date: number;
  duration: number;
  outcome: string | null;
  cash: number;
  prospect: string;
  closer: string;
  showed: boolean;
  closed: boolean;
  firstOptinDate: number | null;
  daysToCall: number | null;
  webinarCount: number;
  totalOptins: number;
  knownMarshFor: string | null;
  callNotes: string;
  optinDetails: OptinDetail[];
  occupation: string;
  hasBrand: string;
  isFullTimeBrandOwner: string;
  brandOwner: string;
  prospectQuestions: number;
  closerQuestions: number;
  prospectAge: number | null;
  prospectGender: string;
  mainObjection: string;
  transcriptOccupation: string;
  transcriptHasBrand: string;
  prospectAspiration: string;
  prospectCameraOn: string;
  prospectConnection: string;
  prospectDevice: string;
  isPaid: boolean;
  sourceType: string;
}

interface StrategyCallsResponse {
  items: StrategyCall[];
  totalCount: number;
  closers: string[];
  occupationStats: Record<string, number>;
  brandStats: Record<string, number>;
  timeBracketStats: Record<string, number>;
  closedTimeBracketStats: Record<string, number>;
  genderStats: Record<string, number>;
  ageBracketStats: Record<string, number>;
  objectionStats: Record<string, number>;
  aspirationStats: Record<string, number>;
  cameraStats: Record<string, number>;
  connectionStats: Record<string, number>;
  deviceStats: Record<string, number>;
}

type SortKey = "date" | "prospect" | "closer" | "duration" | "outcome" | "cash" | "daysToCall" | "webinarCount" | "totalOptins" | "firstOptinDate";

const OUTCOMES = [
  "Closed","Deposit Collected","Taken - Not Closed","Follow-up Scheduled",
  "No Show","Rescheduled","MRR","Downsell","Upsell",
];

const formatDate = (ms: number | undefined | null): string => {
  if (!ms || !Number.isFinite(ms)) return "—";
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatTime = (ms: number | undefined | null): string => {
  if (!ms || !Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const getTimeBracket = (ms: number | undefined | null): string => {
  if (!ms || !Number.isFinite(ms)) return "";
  const h = new Date(ms).getHours();
  if (h < 6)  return "12am–6am";
  if (h < 9)  return "6am–9am";
  if (h < 12) return "9am–12pm";
  if (h < 15) return "12pm–3pm";
  if (h < 18) return "3pm–6pm";
  if (h < 21) return "6pm–9pm";
  return "9pm–12am";
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

interface Tag { label: string; color: string }

// Shared occupation categorizer — must stay in sync with index.ts getOccLabel()
function getOccLabel(occupation: string): string {
  const o = occupation.toLowerCase();
  if (!occupation) return "";
  if (o.includes("college") || o.includes("universit") || o.includes(" uni ") || o === "uni") return "College Student";
  if (o.includes("high school")) return "High School";
  if (o.includes("student")) return "Student";
  if (o.includes("unemployed") || o.includes("not working") || o.includes("no job") || o.includes("jobless") || o.includes("out of work") || o.includes("between job")) return "Unemployed";
  if (o.includes("stay at home") || o.includes("homemaker") || o.includes("sahm") || o.includes("sahd")) return "Stay-at-Home";
  if (o.includes("part time") || o.includes("part-time")) return "Part-Time";
  if (o.includes("9-5") || o.includes("day job") || o.includes("corporate") || o.includes("office job") || o.includes("9 to 5")) return "Day Job";
  if (o.includes("freelance")) return "Freelancer";
  if (o.includes("brand owner") || o.includes("business owner")) return "Brand Owner";
  if (o.includes("entrepreneur") || o.includes("self employ") || o.includes("self-employ")) return "Entrepreneur";
  if (o.includes("employ") || o.includes("working") || o.includes("job") || o.includes("work")) return "Employed";
  return occupation.slice(0, 20);
}

const OCC_COLORS: Record<string, string> = {
  "College Student":  "bg-blue-500/15 text-blue-400 border-blue-500/20",
  "High School":      "bg-sky-500/15 text-sky-400 border-sky-500/20",
  "Student":          "bg-blue-500/15 text-blue-400 border-blue-500/20",
  "Unemployed":       "bg-red-500/15 text-red-400 border-red-500/20",
  "Stay-at-Home":     "bg-pink-500/15 text-pink-400 border-pink-500/20",
  "Part-Time":        "bg-amber-500/15 text-amber-400 border-amber-500/20",
  "Day Job":          "bg-orange-500/15 text-orange-400 border-orange-500/20",
  "Freelancer":       "bg-yellow-500/15 text-yellow-400 border-yellow-500/20",
  "Brand Owner":      "bg-purple-500/15 text-purple-400 border-purple-500/20",
  "Entrepreneur":     "bg-violet-500/15 text-violet-400 border-violet-500/20",
  "Employed":         "bg-orange-500/15 text-orange-400 border-orange-500/20",
};

function computeTags(occupation: string, hasBrand: string, isFullTimeBrandOwner: string, brandOwner: string, transcriptOccupation?: string, transcriptHasBrand?: string): Tag[] {
  const tags: Tag[] = [];
  // Occupation: prefer Application, fall back to transcript-extracted
  let label = getOccLabel(occupation);
  if (!label && transcriptOccupation) {
    const tLabel = getOccLabel(transcriptOccupation);
    if (tLabel) label = tLabel;
  }
  if (label) {
    tags.push({ label, color: OCC_COLORS[label] ?? "bg-muted/40 text-muted-foreground border-border" });
  }
  // Brand: prefer EOC brandOwner → Application hasBrand → transcript-extracted
  const bo = brandOwner.toLowerCase();
  if (bo.includes("yes")) {
    tags.push({ label: "Has Brand", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" });
  } else if (bo.includes("no")) {
    tags.push({ label: "No Brand", color: "bg-muted/40 text-muted-foreground border-border" });
  } else if (hasBrand.toLowerCase() === "yes") {
    tags.push({ label: "Has Brand", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" });
  } else if (hasBrand.toLowerCase() === "no") {
    tags.push({ label: "No Brand", color: "bg-muted/40 text-muted-foreground border-border" });
  } else if ((transcriptHasBrand || "").toLowerCase() === "yes") {
    tags.push({ label: "Has Brand", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" });
  } else if ((transcriptHasBrand || "").toLowerCase() === "no") {
    tags.push({ label: "No Brand", color: "bg-muted/40 text-muted-foreground border-border" });
  }
  if (isFullTimeBrandOwner.toLowerCase() === "yes") {
    tags.push({ label: "Full-Time", color: "bg-indigo-500/15 text-indigo-400 border-indigo-500/20" });
  }
  return tags;
}

// ─── Optin History Modal ──────────────────────────────────────────────────────

function OptinHistoryModal({
  name,
  details,
  onClose,
}: {
  name: string;
  details: OptinDetail[];
  onClose: () => void;
}) {
  const regular = details.filter(d => !d.isWebinar).sort((a, b) => b.date - a.date);
  const webinar = details.filter(d => d.isWebinar).sort((a, b) => b.date - a.date);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h3 className="text-sm font-bold text-foreground">Optin History</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{name}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          {/* Regular optins */}
          {regular.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Regular Opt-ins ({regular.length})
              </p>
              <div className="space-y-1.5">
                {regular.map((d, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2">
                    <span className="text-xs text-foreground">{formatDate(d.date)}</span>
                    {d.source && (
                      <span className="text-[10px] bg-[var(--brand-primary)]/15 text-[var(--brand-primary)] border border-[var(--brand-primary)]/20 rounded px-1.5 py-0.5">
                        {d.source}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Webinar optins */}
          {webinar.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Webinar Opt-ins ({webinar.length})
              </p>
              <div className="space-y-1.5">
                {webinar.map((d, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-2">
                    <span className="text-xs text-foreground">{formatDate(d.date)}</span>
                    <div className="flex items-center gap-1.5">
                      {d.webinarName && (
                        <span className="text-[10px] text-muted-foreground truncate max-w-[120px]">{d.webinarName}</span>
                      )}
                      {d.source && (
                        <span className="text-[10px] bg-purple-500/15 text-purple-400 border border-purple-500/20 rounded px-1.5 py-0.5">
                          {d.source}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {details.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">No optin history found.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sort icon ────────────────────────────────────────────────────────────────

function SortIcon({ column, sortBy, sortDir }: { column: SortKey; sortBy: SortKey; sortDir: "asc" | "desc" }) {
  if (sortBy !== column) return <ChevronsUpDown className="h-3 w-3 ml-1 opacity-40" />;
  return sortDir === "asc"
    ? <ChevronUp className="h-3 w-3 ml-1 text-[var(--brand-primary)]" />
    : <ChevronDown className="h-3 w-3 ml-1 text-[var(--brand-primary)]" />;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function StrategyCalls() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [closer, setCloser] = useState("");
  const [outcome, setOutcome] = useState("");
  const [closedOnly, setClosedOnly] = useState(false);
  const [trafficType, setTrafficType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [durationMin, setDurationMin] = useState("");
  const [durationMax, setDurationMax] = useState("");
  const [sortBy, setSortBy] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState<StrategyCall[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [closers, setClosers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [optinHistory, setOptinHistory] = useState<{ name: string; details: OptinDetail[] } | null>(null);
  const [occupationStats, setOccupationStats] = useState<Record<string, number>>({});
  const [brandStats, setBrandStats] = useState<Record<string, number>>({});
  const [timeBracketStats, setTimeBracketStats] = useState<Record<string, number>>({});
  const [closedTimeBracketStats, setClosedTimeBracketStats] = useState<Record<string, number>>({});
  const [genderStats, setGenderStats] = useState<Record<string, number>>({});
  const [ageBracketStats, setAgeBracketStats] = useState<Record<string, number>>({});
  const [objectionStats, setObjectionStats] = useState<Record<string, number>>({});
  const [aspirationStats, setAspirationStats] = useState<Record<string, number>>({});
  const [cameraStats, setCameraStats] = useState<Record<string, number>>({});
  const [connectionStats, setConnectionStats] = useState<Record<string, number>>({});
  const [deviceStats, setDeviceStats] = useState<Record<string, number>>({});

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchCalls = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: String(page), pageSize: String(pageSize),
        search: debouncedSearch, closer, outcome, sortBy, sortDir,
        ...(closedOnly && { closedOnly: "true" }),
        ...(trafficType && { trafficType }),
        ...(dateFrom && { dateFrom }), ...(dateTo && { dateTo }),
        ...(durationMin && { durationMin }), ...(durationMax && { durationMax }),
      });
      const res = await fetch(`/api/fireflies/strategy-calls?${params}`);
      if (!res.ok) throw new Error((await res.json()).error || "Failed to fetch");
      const json = (await res.json()) as StrategyCallsResponse;
      setItems(json.items);
      setTotalCount(json.totalCount);
      if (json.closers?.length) setClosers(json.closers);
      if (json.occupationStats) setOccupationStats(json.occupationStats);
      if (json.brandStats) setBrandStats(json.brandStats);
      if (json.timeBracketStats) setTimeBracketStats(json.timeBracketStats);
      if (json.closedTimeBracketStats) setClosedTimeBracketStats(json.closedTimeBracketStats);
      if (json.genderStats) setGenderStats(json.genderStats);
      if (json.ageBracketStats) setAgeBracketStats(json.ageBracketStats);
      if (json.objectionStats) setObjectionStats(json.objectionStats);
      if (json.aspirationStats) setAspirationStats(json.aspirationStats);
      if (json.cameraStats) setCameraStats(json.cameraStats);
      if (json.connectionStats) setConnectionStats(json.connectionStats);
      if (json.deviceStats) setDeviceStats(json.deviceStats);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to fetch lead touchpoints");
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, debouncedSearch, closer, outcome, closedOnly, trafficType, sortBy, sortDir, dateFrom, dateTo, durationMin, durationMax]);

  useEffect(() => { fetchCalls(); }, [fetchCalls]);

  const handleSort = (col: SortKey) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
    setPage(1);
  };

  const clearFilters = () => {
    setCloser(""); setOutcome(""); setClosedOnly(false); setTrafficType("");
    setDateFrom(""); setDateTo("");
    setDurationMin(""); setDurationMax(""); setSearch(""); setPage(1);
  };

  const hasActiveFilters = !!(closer || outcome || closedOnly || trafficType || dateFrom || dateTo || durationMin || durationMax || debouncedSearch);

  const SortHeader = ({ col, label }: { col: SortKey; label: string }) => (
    <button onClick={() => handleSort(col)} className="flex items-center font-medium hover:text-[var(--brand-primary)] transition-colors whitespace-nowrap">
      {label}<SortIcon column={col} sortBy={sortBy} sortDir={sortDir} />
    </button>
  );

  const YNBadge = ({ value, trueLabel = "Y", falseLabel = "N" }: { value: boolean; trueLabel?: string; falseLabel?: string }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${value ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-red-500/15 text-red-500"}`}>
      {value ? trueLabel : falseLabel}
    </span>
  );

  const columns: ColumnDef<StrategyCall>[] = [
    {
      id: "prospect",
      header: <SortHeader col="prospect" label="Lead" />,
      cell: (row) => {
        const tags = computeTags(row.occupation, row.hasBrand, row.isFullTimeBrandOwner, row.brandOwner, row.transcriptOccupation, row.transcriptHasBrand);
        const name = row.prospect || row.title;
        const initials = getInitials(name);
        return (
          <div className="flex items-start gap-2.5 min-w-0">
            {/* Initials avatar */}
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-0.5">
              {initials}
            </div>
            <div className="min-w-0">
              <button
                onClick={() => { setSelectedCallId(row.firefliesId); setIsModalOpen(true); }}
                className="font-semibold text-sm text-left hover:text-[var(--brand-primary)] transition-colors max-w-[160px] truncate block"
              >
                {name}
              </button>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {tags.map((t) => (
                    <span key={t.label} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium border ${t.color}`}>
                      {t.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: "closer",
      header: <SortHeader col="closer" label="Closer" />,
      cell: (row) => (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <User className="h-3.5 w-3.5 hidden sm:block shrink-0" />
          <span className="truncate max-w-[120px]">{row.closer || "—"}</span>
        </div>
      ),
    },
    {
      id: "source",
      header: "Source",
      cell: (row) => {
        const st = row.sourceType || "organic";
        const styles: Record<string, string> = {
          paid: "bg-[var(--brand-primary)]/15 text-[var(--brand-primary)] border-[var(--brand-primary)]/20",
          sms: "bg-amber-500/15 text-amber-400 border-amber-500/20",
          email: "bg-violet-500/15 text-violet-400 border-violet-500/20",
          organic: "bg-teal-500/15 text-teal-400 border-teal-500/20",
        };
        const labels: Record<string, string> = { paid: "Paid", sms: "SMS", email: "Email", organic: "Organic" };
        return (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${styles[st] || styles.organic}`}>
            {labels[st] || "Organic"}
          </span>
        );
      },
    },
    {
      id: "date",
      header: <SortHeader col="date" label="Call Date" />,
      cell: (row) => {
        const bracket = getTimeBracket(row.date);
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
              <Calendar className="h-3.5 w-3.5 hidden sm:block shrink-0" />
              {formatDate(row.date)}
            </div>
            {bracket && (
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-muted-foreground/60">{formatTime(row.date)}</span>
                <span className="text-[9px] bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border border-[var(--brand-primary)]/20 rounded px-1 py-0.5 font-medium">
                  {bracket}
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "showed",
      header: "Showed",
      cell: (row) => <YNBadge value={row.showed} />,
    },
    {
      id: "closed",
      header: "Closed",
      cell: (row) => <YNBadge value={row.closed} />,
    },
    {
      id: "cash",
      header: <SortHeader col="cash" label="Cash" />,
      cell: (row) => row.cash > 0
        ? <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400 whitespace-nowrap">${row.cash.toLocaleString()}</span>
        : <span className="text-muted-foreground/40">—</span>,
    },
    {
      id: "daysToCall",
      header: <SortHeader col="daysToCall" label="Days→Call" />,
      cell: (row) => row.daysToCall !== null && row.daysToCall >= 0
        ? <span className="text-sm text-muted-foreground">{row.daysToCall}d</span>
        : <span className="text-muted-foreground/40">—</span>,
    },
    {
      id: "firstOptinDate",
      header: <SortHeader col="firstOptinDate" label="1st Optin" />,
      cell: (row) => (
        <span className="text-sm text-muted-foreground whitespace-nowrap">{formatDate(row.firstOptinDate)}</span>
      ),
    },
    {
      id: "webinarCount",
      header: <SortHeader col="webinarCount" label="Webinars" />,
      cell: (row) => {
        const webinarOptins = row.optinDetails?.filter(d => d.isWebinar) ?? [];
        if (row.webinarCount === 0) return <span className="text-muted-foreground/40">—</span>;
        return (
          <button
            onClick={() => setOptinHistory({ name: row.prospect || row.title, details: row.optinDetails ?? [] })}
            className="text-sm font-medium text-[var(--brand-primary)] hover:underline"
            title="Click to see webinar optin history"
          >
            {webinarOptins.length > 0 ? webinarOptins.length : row.webinarCount}×
          </button>
        );
      },
    },
    {
      id: "totalOptins",
      header: <SortHeader col="totalOptins" label="Optins" />,
      cell: (row) => {
        if (row.totalOptins === 0) return <span className="text-muted-foreground/40">—</span>;
        return (
          <button
            onClick={() => setOptinHistory({ name: row.prospect || row.title, details: row.optinDetails ?? [] })}
            className="text-sm font-medium text-[var(--brand-primary)] hover:underline"
            title="Click to see full optin history"
          >
            {row.totalOptins}×
          </button>
        );
      },
    },
    {
      id: "brandOwner",
      header: "Brand",
      cell: (row) => {
        const bo = row.brandOwner.toLowerCase();
        const appBrand = row.hasBrand.toLowerCase();
        const tBrand = (row.transcriptHasBrand || "").toLowerCase();
        if (bo.includes("yes") || (!bo && appBrand === "yes") || (!bo && !appBrand && tBrand === "yes"))
          return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">Yes</span>;
        if (bo.includes("no") || (!bo && appBrand === "no") || (!bo && !appBrand && tBrand === "no"))
          return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted/40 text-muted-foreground border border-border">No</span>;
        return <span className="text-muted-foreground/40">—</span>;
      },
    },
    {
      id: "age",
      header: "Age",
      cell: (row) => {
        const age = row.prospectAge;
        if (!age || age <= 0) return <span className="text-muted-foreground/40">—</span>;
        return <span className="text-sm font-medium text-foreground">{age}</span>;
      },
    },
    {
      id: "gender",
      header: "Gender",
      cell: (row) => {
        const g = row.prospectGender;
        if (!g || g === "Unknown") return <span className="text-muted-foreground/40">—</span>;
        return (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
            g === "Male"
              ? "bg-blue-500/15 text-blue-400 border-blue-500/20"
              : "bg-pink-500/15 text-pink-400 border-pink-500/20"
          }`}>{g}</span>
        );
      },
    },
    {
      id: "objection",
      header: "Objection",
      cell: (row) => {
        const obj = row.mainObjection;
        if (!obj || obj === "Unknown" || obj === "N/A" || obj === "Analysis Failed") return <span className="text-muted-foreground/40">—</span>;
        if (obj.toLowerCase().includes("none") || obj.toLowerCase().includes("closed")) {
          return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">None</span>;
        }
        return (
          <div className="group relative max-w-[120px]">
            <span className="text-xs text-amber-400 truncate block cursor-default">{obj}</span>
            <div className="pointer-events-none absolute bottom-full left-0 mb-2 z-50 hidden group-hover:block rounded-lg border border-border bg-card px-3 py-2 shadow-xl">
              <p className="text-xs text-foreground whitespace-nowrap">{obj}</p>
            </div>
          </div>
        );
      },
    },
    {
      id: "aspiration",
      header: "Aspiration",
      cell: (row) => {
        const asp = row.prospectAspiration;
        if (!asp || asp === "Unknown" || asp === "N/A") return <span className="text-muted-foreground/40">--</span>;
        return (
          <div className="group relative max-w-[120px]">
            <span className="text-xs text-teal-400 truncate block cursor-default">{asp}</span>
            <div className="pointer-events-none absolute bottom-full left-0 mb-2 z-50 hidden group-hover:block rounded-lg border border-border bg-card px-3 py-2 shadow-xl">
              <p className="text-xs text-foreground whitespace-nowrap">{asp}</p>
            </div>
          </div>
        );
      },
    },
    {
      id: "camera",
      header: "Camera",
      cell: (row) => {
        const cam = row.prospectCameraOn;
        if (!cam || cam === "Unknown") return <span className="text-muted-foreground/40">--</span>;
        if (cam.toLowerCase() === "yes" || cam.toLowerCase() === "on")
          return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">On</span>;
        if (cam.toLowerCase() === "no" || cam.toLowerCase() === "off")
          return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/20">Off</span>;
        return <span className="text-xs text-muted-foreground">{cam}</span>;
      },
    },
    {
      id: "connection",
      header: "Connection",
      cell: (row) => {
        const conn = row.prospectConnection;
        if (!conn || conn === "Unknown") return <span className="text-muted-foreground/40">--</span>;
        if (conn.toLowerCase() === "good" || conn.toLowerCase() === "stable")
          return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">Good</span>;
        if (conn.toLowerCase() === "choppy" || conn.toLowerCase() === "poor" || conn.toLowerCase() === "bad")
          return <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/20">{conn}</span>;
        return <span className="text-xs text-muted-foreground">{conn}</span>;
      },
    },
    {
      id: "device",
      header: "Device",
      cell: (row) => {
        const dev = row.prospectDevice;
        if (!dev || dev === "Unknown") return <span className="text-muted-foreground/40">--</span>;
        return <span className="text-xs text-muted-foreground">{dev}</span>;
      },
    },
    {
      id: "callDuration",
      header: "Duration",
      cell: (row) => {
        const mins = Math.round(row.duration / 60);
        if (mins <= 0) return <span className="text-muted-foreground/40">—</span>;
        return <span className="text-sm text-muted-foreground">{mins}m</span>;
      },
    },
    {
      id: "questions",
      header: "Q/Call",
      cell: (row) => {
        const pQ = row.prospectQuestions ?? 0;
        const cQ = row.closerQuestions ?? 0;
        if (pQ === 0 && cQ === 0) return <span className="text-muted-foreground/40">—</span>;
        return (
          <div className="text-xs space-y-0.5" title={`Prospect asked ${pQ} questions · Closer asked ${cQ} questions`}>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">P:</span>
              <span className="font-medium text-foreground">{pQ}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground">C:</span>
              <span className="font-medium text-[var(--brand-primary)]">{cQ}</span>
            </div>
          </div>
        );
      },
    },
    {
      id: "callNotes",
      header: "Notes",
      cell: (row) => {
        if (!row.callNotes) return <span className="text-muted-foreground/40">—</span>;
        return (
          <div className="group relative max-w-[180px]">
            <p className="text-xs text-muted-foreground truncate cursor-default">{row.callNotes}</p>
            {/* Full note tooltip */}
            <div className="pointer-events-none absolute bottom-full left-0 mb-2 z-50 hidden group-hover:block w-72 rounded-lg border border-border bg-card p-3 shadow-xl">
              <p className="text-xs text-foreground leading-relaxed whitespace-pre-wrap">{row.callNotes}</p>
            </div>
          </div>
        );
      },
    },
    {
      id: "knownMarshFor",
      header: "Known Marsh",
      cell: (row) => row.knownMarshFor
        ? <span className="text-sm text-muted-foreground capitalize">{row.knownMarshFor}</span>
        : <span className="text-muted-foreground/40">—</span>,
    },
    {
      id: "action",
      header: "",
      cell: (row) => (
        <Button
          size="sm"
          onClick={() => { setSelectedCallId(row.firefliesId); setIsModalOpen(true); }}
          className="bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white font-medium px-3 hover:shadow-lg hover:shadow-[var(--brand-primary)]/25 transition-all"
        >
          <Play className="h-3.5 w-3.5 sm:mr-1.5" />
          <span className="hidden sm:inline">View</span>
        </Button>
      ),
      className: "text-right",
      headerClassName: "text-right",
    },
  ];

  const totalCash = items.reduce((s, i) => s + i.cash, 0);
  const closedCount = items.filter(i => i.closed).length;
  const showedCount = items.filter(i => i.showed).length;
  const aov = closedCount > 0 ? totalCash / closedCount : 0;

  return (
    <div className="space-y-6">
      <div className="opacity-0 animate-fade-in-up">
        <h2 className="text-2xl font-bold mb-2">Lead Patterns</h2>
        <p className="text-muted-foreground">Closing call recordings from Fireflies.ai</p>
      </div>

      <div className="bg-card rounded-2xl p-4 sm:p-6 shadow-brand opacity-0 animate-fade-in-up stagger-1 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by prospect name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-secondary border-border h-11"
            />
          </div>
          <select
            value={closer}
            onChange={(e) => { setCloser(e.target.value); setPage(1); }}
            className="h-11 rounded-lg border border-border bg-secondary px-3 text-sm text-foreground min-w-[160px]"
          >
            <option value="">All Closers</option>
            {closers.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={outcome}
            onChange={(e) => { setOutcome(e.target.value); setPage(1); }}
            className="h-11 rounded-lg border border-border bg-secondary px-3 text-sm text-foreground min-w-[170px]"
          >
            <option value="">All Outcomes</option>
            {OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          {/* Closed only toggle */}
          <button
            onClick={() => { setClosedOnly(v => !v); setPage(1); }}
            className={`h-11 px-4 rounded-lg border text-sm font-medium transition-colors whitespace-nowrap ${
              closedOnly
                ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-400"
                : "border-border bg-secondary text-muted-foreground hover:text-foreground"
            }`}
          >
            Closed Only
          </button>
          {/* Traffic source filter */}
          <select
            value={trafficType}
            onChange={(e) => { setTrafficType(e.target.value); setPage(1); }}
            className="h-11 rounded-lg border border-border bg-secondary px-3 text-sm text-foreground min-w-[140px]"
          >
            <option value="">All Sources</option>
            <option value="paid">Paid Ads</option>
            <option value="organic">Organic</option>
            <option value="sms">SMS</option>
            <option value="email">Email</option>
          </select>
          <Button
            variant="outline"
            onClick={() => setShowFilters(f => !f)}
            className="flex items-center gap-2 h-11 whitespace-nowrap"
          >
            <Filter className="h-4 w-4" />
            More Filters
            {hasActiveFilters && <span className="h-2 w-2 rounded-full bg-[var(--brand-primary)]" />}
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground gap-1 h-11">
              <X className="h-3.5 w-3.5" /> Clear
            </Button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-border">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground font-medium">Date From</label>
              <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="h-9 bg-secondary border-border text-sm" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground font-medium">Date To</label>
              <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="h-9 bg-secondary border-border text-sm" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground font-medium">Min Duration (min)</label>
              <Input type="number" placeholder="e.g. 30" value={durationMin} onChange={(e) => { setDurationMin(e.target.value); setPage(1); }} className="h-9 bg-secondary border-border text-sm" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground font-medium">Max Duration (min)</label>
              <Input type="number" placeholder="e.g. 90" value={durationMax} onChange={(e) => { setDurationMax(e.target.value); setPage(1); }} className="h-9 bg-secondary border-border text-sm" />
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-5 text-sm text-muted-foreground pt-1">
          <div className="flex items-center gap-1.5">
            <Play className="h-4 w-4 text-[var(--brand-primary)]" />
            <span><strong className="text-foreground">{totalCount}</strong> calls</span>
          </div>
          <div className="flex items-center gap-1.5">
            <User className="h-4 w-4 text-[var(--brand-primary)]" />
            <span><strong className="text-foreground">{showedCount}</strong> showed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <DollarSign className="h-4 w-4 text-emerald-500" />
            <span><strong className="text-emerald-600 dark:text-emerald-400">${totalCash.toLocaleString()}</strong> cash collected</span>
          </div>
          {closedCount > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              <span><strong className="text-foreground">{closedCount}</strong> closed</span>
            </div>
          )}
          {aov > 0 && (
            <div className="flex items-center gap-1.5">
              <DollarSign className="h-4 w-4 text-[var(--brand-primary)]" />
              <span><strong className="text-[var(--brand-primary)]">${Math.round(aov).toLocaleString()}</strong> AOV</span>
            </div>
          )}
        </div>
      </div>

      {/* ─── Charts ──────────────────────────────────────────────────────── */}
      {(Object.keys(occupationStats).length > 0 || Object.keys(brandStats).length > 0 || Object.keys(timeBracketStats).length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-0 animate-fade-in-up stagger-1">
          {/* Occupation / Background breakdown */}
          {Object.keys(occupationStats).length > 0 && (() => {
            const data = Object.entries(occupationStats)
              .filter(([, v]) => v > 0)
              .sort((a, b) => b[1] - a[1])
              .map(([name, value]) => ({ name, value }));
            const total = data.reduce((s, d) => s + d.value, 0);
            const COLORS = [
              branding.colors.chart[0], branding.colors.chart[1], branding.colors.chart[2],
              branding.colors.chart[3], branding.colors.chart[4], branding.colors.gradient.start,
            ];
            return (
              <div className="bg-card rounded-2xl p-5 shadow-brand">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Lead Background</p>
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width={140} height={140}>
                    <PieChart>
                      <Pie data={data} cx="50%" cy="50%" innerRadius={38} outerRadius={60} dataKey="value" strokeWidth={2} stroke="transparent">
                        {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}
                        formatter={(value: number, name: string) => [`${value} (${Math.round(value / total * 100)}%)`, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-1.5 min-w-0">
                    {data.map((d, i) => (
                      <div key={d.name} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                          <span className="text-xs text-muted-foreground truncate">{d.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs font-medium text-foreground">{d.value}</span>
                          <span className="text-[10px] text-muted-foreground/60">{Math.round(d.value / total * 100)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Has Brand vs No Brand */}
          {Object.keys(brandStats).length > 0 && (() => {
            const BRAND_COLORS: Record<string, string> = {
              "Has Brand": branding.colors.chart[0],
              "No Brand": branding.colors.chart[4],
              "Unknown": branding.colors.chart[2],
            };
            const data = Object.entries(brandStats)
              .filter(([, v]) => v > 0)
              .sort((a, b) => b[1] - a[1])
              .map(([name, value]) => ({ name, value }));
            const total = data.reduce((s, d) => s + d.value, 0);
            return (
              <div className="bg-card rounded-2xl p-5 shadow-brand">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Brand Status</p>
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width={140} height={140}>
                    <PieChart>
                      <Pie data={data} cx="50%" cy="50%" innerRadius={38} outerRadius={60} dataKey="value" strokeWidth={2} stroke="transparent">
                        {data.map((d) => <Cell key={d.name} fill={BRAND_COLORS[d.name] ?? branding.colors.chart[2]} />)}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}
                        formatter={(value: number, name: string) => [`${value} (${Math.round(value / total * 100)}%)`, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-1.5">
                    {data.map((d) => (
                      <div key={d.name} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: BRAND_COLORS[d.name] ?? branding.colors.chart[2] }} />
                          <span className="text-xs text-muted-foreground">{d.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs font-medium text-foreground">{d.value}</span>
                          <span className="text-[10px] text-muted-foreground/60">{Math.round(d.value / total * 100)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* All Calls by Time Bracket */}
          {(() => {
            const data = Object.entries(timeBracketStats).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
            if (data.length === 0) return null;
            const total = data.reduce((s, d) => s + d.value, 0);
            const COLORS = [
              branding.colors.chart[0], branding.colors.chart[1], branding.colors.chart[2],
              branding.colors.chart[3], branding.colors.chart[4], branding.colors.gradient.start, branding.colors.gradient.end,
            ];
            return (
              <div className="bg-card rounded-2xl p-5 shadow-brand">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">All Calls by Time</p>
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width={140} height={140}>
                    <PieChart>
                      <Pie data={data} cx="50%" cy="50%" innerRadius={38} outerRadius={60} dataKey="value" strokeWidth={2} stroke="transparent">
                        {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}
                        formatter={(value: number, name: string) => [`${value} (${Math.round(value / total * 100)}%)`, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-1.5 min-w-0">
                    {data.map((d, i) => (
                      <div key={d.name} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                          <span className="text-xs text-muted-foreground truncate">{d.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs font-medium text-foreground">{d.value}</span>
                          <span className="text-[10px] text-muted-foreground/60">{Math.round(d.value / total * 100)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Closed Calls by Time Bracket */}
          {(() => {
            const data = Object.entries(closedTimeBracketStats).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
            if (data.length === 0) return null;
            const total = data.reduce((s, d) => s + d.value, 0);
            const COLORS = [
              branding.colors.chart[0], branding.colors.chart[1], branding.colors.chart[2],
              branding.colors.chart[3], branding.colors.chart[4], branding.colors.gradient.start, branding.colors.gradient.end,
            ];
            return (
              <div className="bg-card rounded-2xl p-5 shadow-brand">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Closes by Time</p>
                <p className="text-[10px] text-muted-foreground/60 mb-4">Which time slots convert best</p>
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width={140} height={140}>
                    <PieChart>
                      <Pie data={data} cx="50%" cy="50%" innerRadius={38} outerRadius={60} dataKey="value" strokeWidth={2} stroke="transparent">
                        {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}
                        formatter={(value: number, name: string) => [`${value} closes (${Math.round(value / total * 100)}%)`, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-1.5 min-w-0">
                    {data.map((d, i) => (
                      <div key={d.name} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                          <span className="text-xs text-muted-foreground truncate">{d.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs font-medium text-emerald-500">{d.value}</span>
                          <span className="text-[10px] text-muted-foreground/60">{Math.round(d.value / total * 100)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Gender, Age Bracket, and Objection pie charts */}
      {(Object.keys(genderStats).length > 0 || Object.keys(ageBracketStats).length > 0 || Object.keys(objectionStats).length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 opacity-0 animate-fade-in-up stagger-2">
          {/* Gender */}
          {Object.keys(genderStats).length > 0 && (() => {
            const data = Object.entries(genderStats).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
            const total = data.reduce((s, d) => s + d.value, 0);
            const GENDER_COLORS: Record<string, string> = { Male: branding.colors.chart[1], Female: "#ec4899", Unknown: "#6b7280" };
            return (
              <div className="bg-card rounded-2xl p-5 shadow-brand">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Gender</p>
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width={140} height={140}>
                    <PieChart>
                      <Pie data={data} cx="50%" cy="50%" innerRadius={38} outerRadius={60} dataKey="value" strokeWidth={2} stroke="transparent">
                        {data.map((d) => <Cell key={d.name} fill={GENDER_COLORS[d.name] ?? branding.colors.chart[3]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} formatter={(value: number, name: string) => [`${value} (${Math.round(value / total * 100)}%)`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-1.5 min-w-0">
                    {data.map((d) => (
                      <div key={d.name} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: GENDER_COLORS[d.name] ?? branding.colors.chart[3] }} />
                          <span className="text-xs text-muted-foreground truncate">{d.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs font-medium text-foreground">{d.value}</span>
                          <span className="text-[10px] text-muted-foreground/60">{Math.round(d.value / total * 100)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Age Brackets */}
          {Object.keys(ageBracketStats).length > 0 && (() => {
            const AGE_ORDER = ["Under 18", "18-20", "21-24", "25-29", "30-34", "35-39", "40+", "Unknown"];
            const data = AGE_ORDER
              .filter(k => (ageBracketStats[k] ?? 0) > 0)
              .map(name => ({ name, value: ageBracketStats[name] ?? 0 }));
            const total = data.reduce((s, d) => s + d.value, 0);
            const COLORS = [
              branding.colors.chart[0], branding.colors.chart[1], branding.colors.chart[2],
              branding.colors.chart[3], branding.colors.chart[4], branding.colors.gradient.start, branding.colors.gradient.end, "#6b7280",
            ];
            return (
              <div className="bg-card rounded-2xl p-5 shadow-brand">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Age Brackets</p>
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width={140} height={140}>
                    <PieChart>
                      <Pie data={data} cx="50%" cy="50%" innerRadius={38} outerRadius={60} dataKey="value" strokeWidth={2} stroke="transparent">
                        {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} formatter={(value: number, name: string) => [`${value} (${Math.round(value / total * 100)}%)`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-1.5 min-w-0">
                    {data.map((d, i) => (
                      <div key={d.name} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                          <span className="text-xs text-muted-foreground truncate">{d.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs font-medium text-foreground">{d.value}</span>
                          <span className="text-[10px] text-muted-foreground/60">{Math.round(d.value / total * 100)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Main Objections */}
          {Object.keys(objectionStats).length > 0 && (() => {
            const data = Object.entries(objectionStats)
              .map(([name, value]) => ({ name, value }))
              .sort((a, b) => b.value - a.value)
              .slice(0, 8);
            const total = data.reduce((s, d) => s + d.value, 0);
            const COLORS = [
              branding.colors.chart[0], branding.colors.chart[1], branding.colors.chart[2],
              branding.colors.chart[3], branding.colors.chart[4], branding.colors.gradient.start, branding.colors.gradient.end, "#6b7280",
            ];
            return (
              <div className="bg-card rounded-2xl p-5 shadow-brand">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Main Objections</p>
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width={140} height={140}>
                    <PieChart>
                      <Pie data={data} cx="50%" cy="50%" innerRadius={38} outerRadius={60} dataKey="value" strokeWidth={2} stroke="transparent">
                        {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} formatter={(value: number, name: string) => [`${value} (${Math.round(value / total * 100)}%)`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-1.5 min-w-0">
                    {data.map((d, i) => (
                      <div key={d.name} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                          <span className="text-xs text-muted-foreground truncate">{d.name}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-xs font-medium text-foreground">{d.value}</span>
                          <span className="text-[10px] text-muted-foreground/60">{Math.round(d.value / total * 100)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Aspirations pie chart */}
      {Object.keys(aspirationStats).length > 0 && (() => {
        const data = Object.entries(aspirationStats)
          .filter(([name]) => name !== "Unknown")
          .map(([name, value]) => ({ name, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 8);
        if (data.length === 0) return null;
        const total = data.reduce((s, d) => s + d.value, 0);
        const COLORS = [
          branding.colors.chart[0], branding.colors.chart[1], branding.colors.chart[2],
          branding.colors.chart[3], branding.colors.chart[4], branding.colors.gradient.start, branding.colors.gradient.end, "#6b7280",
        ];
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-0 animate-fade-in-up stagger-2">
            <div className="bg-card rounded-2xl p-5 shadow-brand">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Aspirations</p>
              <div className="flex items-center gap-4">
                <ResponsiveContainer width={140} height={140}>
                  <PieChart>
                    <Pie data={data} cx="50%" cy="50%" innerRadius={38} outerRadius={60} dataKey="value" strokeWidth={2} stroke="transparent">
                      {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }} formatter={(value: number, name: string) => [`${value} (${Math.round(value / total * 100)}%)`, name]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-1.5 min-w-0">
                  {data.map((d, i) => (
                    <div key={d.name} className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="text-xs text-muted-foreground truncate">{d.name}</span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-xs font-medium text-foreground">{d.value}</span>
                        <span className="text-[10px] text-muted-foreground/60">{Math.round(d.value / total * 100)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-xl p-4">
          <p className="font-medium">Error</p>
          <p className="text-sm mt-1">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchCalls} className="mt-3">Try Again</Button>
        </div>
      )}

      <div className="opacity-0 animate-fade-in-up stagger-2">
        <DataTable
          data={items}
          columns={columns}
          rowKey="_id"
          loading={isLoading}
          emptyMessage={hasActiveFilters ? "No lead touchpoints match your filters." : "No lead touchpoints found."}
          serverPagination={{
            currentPage: page,
            pageSize,
            totalItems: totalCount,
            totalPages: Math.ceil(totalCount / pageSize),
            pageSizeOptions: [10, 25, 50, 100],
            onPageChange: setPage,
            onPageSizeChange: (s) => { setPageSize(s); setPage(1); },
          }}
        />
      </div>

      <CallDetailModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedCallId(null); }}
        firefliesId={selectedCallId}
      />

      {optinHistory && (
        <OptinHistoryModal
          name={optinHistory.name}
          details={optinHistory.details}
          onClose={() => setOptinHistory(null)}
        />
      )}
    </div>
  );
}

export default StrategyCalls;
